import { PerspectiveCamera, Vector3 } from 'three';

import { store } from 'src/app/store';
import {
  cameraTransitionStarted,
  cameraTransitionCompleted,
} from 'src/state';

import { easeInOutCubic } from './easingFunctions';
import { isDebugging } from './urlParams';
import { transitionTime } from 'src/constants';

const zoomInSpeed = 4;

export class ScatterCamera extends PerspectiveCamera {
  private progress: number = 0;

  public target: Vector3 = new Vector3();
  private newTarget?: Vector3;
  private previousTarget?: Vector3;

  private newPosition?: Vector3;
  private previousPosition?: Vector3;

  private forward: Vector3 = new Vector3();
  private forwardDistanceCovered: number = 0;
  private forwardMax: number = 0;

  public update = (delta: number, elapsedTime: number) => {
    if (this.isTransitioning()) this.progress += Math.min(delta / transitionTime, 1);
    this.updatePosition(delta, elapsedTime);
    this.updateLookAt(delta);
    if (this.isTransitioning && this.progress >= 1) this.completeTransition();
  }

  private updatePosition(delta: number, elapsedTime: number): void {
    if (this.newPosition && this.previousPosition) {
      this.position.lerpVectors(
        this.previousPosition,
        this.newPosition,
        easeInOutCubic(this.progress),
      );
    } else if (!isDebugging && this.forwardDistanceCovered < this.forwardMax) {
      const distanceToMove = this.forward.clone().multiplyScalar(delta * zoomInSpeed);
      this.forwardDistanceCovered += distanceToMove.length();
      this.position.add(distanceToMove);
    }
  }

  private updateLookAt(delta: number): void {
    if (this.newTarget && this.previousTarget) {
      this.target.lerpVectors(
        this.previousTarget,
        this.newTarget,
        easeInOutCubic(this.progress),
      );
    }

    this.lookAt(this.target);
  }

  public transitionPositionAndTarget(newPosition: Vector3, newTarget: Vector3, instant: boolean) {
    this.forwardDistanceCovered = 0;
    this.forward = newTarget.clone().sub(newPosition);
    this.forwardMax = this.forward.length() * 0.1;
    this.forward.normalize();

    if (instant) {
      this.forwardDistanceCovered = this.forwardMax;
      this.position.set(
        newPosition.x,
        newPosition.y,
        newPosition.z,
      );
      this.target.set(
        newTarget.x,
        newTarget.y,
        newTarget.z,
      );
      this.lookAt(newTarget);
      return;
    }


    store.dispatch(cameraTransitionStarted());
    this.newTarget = newTarget;
    this.previousTarget = this.target.clone();

    this.newPosition = newPosition;
    this.previousPosition = this.position.clone();
  }

  public isTransitioning(): boolean {
    return !!(this.newTarget && this.previousTarget && this.newPosition && this.previousPosition);
  }

  private completeTransition() {
    this.progress = 0;
    this.newTarget = undefined;
    this.previousTarget = undefined;
    this.newPosition = undefined;
    this.previousPosition = undefined;
    store.dispatch(cameraTransitionCompleted());
  }
}
